import type { ManagedReturnsTrialSubmissionInput } from "@redotech/merchant-sdk/rpc/schema/merchant-submissions/submit-managed-returns-trial";
import { useHandler } from "@redotech/react-util/hook";
import RedoLogo from "@redotech/redo-icons/redo-logo-cutout.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { alertOnFailure } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { useRpcMutation } from "@redotech/redo-web/rpc-query/use-rpc-mutation";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useRef, useState } from "react";
import warehouseImageUrl from "./assets/warehouse-graphic.png?url";
import {
  getAverageProcessingTimeLabel,
  isAutoProcessingEnabled,
} from "./managed-returns-trial-audience";
import * as styles from "./managed-returns-trial-modal.module.css";
import { StepContent } from "./managed-returns-trial-pages";
import {
  getManagedReturnsTrialSubmissionInput,
  isManagedReturnsTrialSubmissionStep,
} from "./managed-returns-trial-submission";

export type ReturnsManagement =
  ManagedReturnsTrialSubmissionInput["returnsManagement"];
export type ProductHandling =
  ManagedReturnsTrialSubmissionInput["productHandling"];
export type StepId =
  | "hook"
  | "manage-today"
  | "processing"
  | "refulfillment"
  | "consolidation"
  | "confirmation";

export interface FormState {
  returnsManagement: ReturnsManagement | null;
  productHandling: ProductHandling | null;
}

interface StepMeta {
  id: StepId;
  title: string;
}

interface ManagedReturnsTrialModalProps {
  onClose: () => void;
  onComplete?: () => void;
}

const DEFAULT_FORM: FormState = {
  returnsManagement: null,
  productHandling: null,
};

const BASE_STEPS: StepMeta[] = [
  { id: "hook", title: "30-day free trial" },
  { id: "manage-today", title: "How you manage returns" },
  { id: "processing", title: "How processing works" },
];

const CONFIRMATION_STEP: StepMeta = {
  id: "confirmation",
  title: "We'll be in touch",
};

function visibleSteps(productHandling: ProductHandling | null): StepMeta[] {
  if (productHandling === "fulfill") {
    return [
      ...BASE_STEPS,
      { id: "refulfillment", title: "How refulfillment works" },
      CONFIRMATION_STEP,
    ];
  }
  if (productHandling === "consolidate") {
    return [
      ...BASE_STEPS,
      { id: "consolidation", title: "How consolidation works" },
      CONFIRMATION_STEP,
    ];
  }
  return BASE_STEPS;
}

export const ManagedReturnsTrialModal = memo(function ManagedReturnsTrialModal({
  onClose,
  onComplete,
}: ManagedReturnsTrialModalProps) {
  const team = useContext(TeamContext);
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const scrollRef = useRef<HTMLDivElement>(null);
  const submissionMutation = useRpcMutation(RedoMerchantRpcClientContext, {
    method: "submitManagedReturnsTrial",
  });

  const isAutoProcessing = isAutoProcessingEnabled({
    autoProcessGreenReturns: team?.settings?.autoProcessGreenReturns,
    autoProcessGreenReturnTypes: team?.settings?.autoProcessGreenReturnTypes,
    returnProcessingAutomation: team?.automation?.returnProcessing
      ? {
          enabled: team.automation.enable,
          exchanges: team.automation.returnProcessing.exchanges,
          refunds: team.automation.returnProcessing.refunds,
          storeCredit: team.automation.returnProcessing.store_credit,
        }
      : undefined,
  });
  const analyticsEndDate = Temporal.Now.plainDateISO();
  const processingStatsQuery = useRpcQuery(RedoMerchantRpcClientContext, {
    method: "getReturnOperationsKeyStats",
    args: {
      startDate: analyticsEndDate.subtract({ days: 90 }).toString(),
      endDate: analyticsEndDate.toString(),
      types: [ReturnTypeEnum.RETURN],
      salesChannels: [],
    },
    options: { enabled: !isAutoProcessing },
  });
  const averageProcessingTimeLabel = processingStatsQuery.data
    ? getAverageProcessingTimeLabel(
        processingStatsQuery.data.avgDaysToProcess,
        processingStatsQuery.data.numProcessedComplete,
      )
    : null;

  const steps = visibleSteps(form.productHandling);
  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const step = steps[safeIndex]!;
  const isConfirmation = step.id === "confirmation";

  // Confirmation is excluded from the total; the branch slide isn't in `steps`
  // until a handling choice is made, so count it explicitly beforehand.
  const progressTotal =
    form.productHandling === null ? BASE_STEPS.length + 1 : steps.length - 1;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [safeIndex]);

  const handleBack = useHandler(() => {
    setStepIndex(Math.max(0, safeIndex - 1));
  });

  const handleContinue = useHandler(async () => {
    if (!isManagedReturnsTrialSubmissionStep(step.id)) {
      setStepIndex(safeIndex + 1);
      return;
    }

    const submissionInput = getManagedReturnsTrialSubmissionInput(
      step.id,
      form,
    );
    if (submissionInput === null) {
      return;
    }

    await alertOnFailure("Failed to submit your responses")(async () => {
      await submissionMutation.mutateAsync(submissionInput);
      setStepIndex(safeIndex + 1);
    });
  });

  const handleRestart = useHandler(() => {
    setForm(DEFAULT_FORM);
    setStepIndex(0);
  });

  const handleFinish = useHandler(() => {
    if (onComplete) {
      onComplete();
      return;
    }
    onClose();
  });

  const selectReturnsManagement = useHandler((value: ReturnsManagement) => {
    setForm((prev) => ({ ...prev, returnsManagement: value }));
  });

  const selectProductHandling = useHandler((value: ProductHandling) => {
    setForm((prev) => ({ ...prev, productHandling: value }));
  });

  const continueDisabled =
    (step.id === "manage-today" && form.returnsManagement === null) ||
    (step.id === "processing" && form.productHandling === null);

  const primaryText =
    step.id === "hook"
      ? "Get started for free"
      : step.id === "refulfillment" || step.id === "consolidation"
        ? "Finish"
        : "Continue";

  return (
    <RedoModal
      className={styles.modal}
      hideFooter
      hideHeader
      isOpen
      mainContentClassName={styles.main}
      modalSize="xl"
      noPadding
      onModalCloseRequested={onClose}
      title="Redo Managed Returns trial onboarding"
      uiSelector="managed-returns-trial-modal"
    >
      <div className={styles.shell}>
        <div className={styles.contentColumn}>
          <header className={styles.header}>
            <div className={styles.brandLockup}>
              <RedoLogo className={styles.brandLogo} />
              <Text fontSize="sm" fontWeight="medium">
                Redo Managed Returns
              </Text>
            </div>
            {!isConfirmation && (
              <ProgressBar current={safeIndex} total={progressTotal} />
            )}
          </header>
          <div className={styles.content} ref={scrollRef}>
            <StepContent
              averageProcessingTimeLabel={averageProcessingTimeLabel}
              form={form}
              isAutoProcessing={isAutoProcessing}
              onSelectProductHandling={selectProductHandling}
              onSelectReturnsManagement={selectReturnsManagement}
              stepId={step.id}
            />
          </div>
          <footer className={styles.footer}>
            {isConfirmation ? (
              <>
                <div>
                  <RedoButton
                    hierarchy="secondary"
                    onClick={handleRestart}
                    size="sm"
                    text="Start over"
                  />
                </div>
                <div>
                  <RedoButton
                    hierarchy="primary"
                    onClick={handleFinish}
                    size="sm"
                    text="Done"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  {safeIndex > 0 && (
                    <RedoButton
                      hierarchy="secondary"
                      onClick={handleBack}
                      size="sm"
                      text="Back"
                    />
                  )}
                </div>
                <div>
                  <RedoButton
                    disabled={continueDisabled || submissionMutation.isPending}
                    hierarchy="primary"
                    onClick={handleContinue}
                    pending={submissionMutation.isPending}
                    size="sm"
                    text={primaryText}
                  />
                </div>
              </>
            )}
          </footer>
        </div>
        <MarketingPanel onClose={onClose} />
      </div>
    </RedoModal>
  );
});

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = total > 0 ? ((current + 1) / total) * 100 : 0;
  return (
    <div className={styles.progressTrack}>
      <div className={styles.progressFill} style={{ width: `${pct}%` }} />
    </div>
  );
}

function MarketingPanel({ onClose }: { onClose: () => void }) {
  return (
    <aside className={styles.marketingPanel}>
      <img alt="" className={styles.marketingImage} src={warehouseImageUrl} />
      <button
        aria-label="Close managed returns trial onboarding"
        className={styles.closeButton}
        onClick={onClose}
        type="button"
      >
        <XCloseIcon />
      </button>
    </aside>
  );
}
